/**
 * Main Warehouse Layout Editor Component
 * Orchestrates 2D editing and 3D visualization with multi-select and bulk placement
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft,
  Save,
  Download,
  Upload,
  Grid3X3,
  Tag,
  Flame,
  Radio,
  RotateCcw,
  Trash2,
  Plus,
  Settings,
  Box,
  Loader2,
  AlertCircle,
  CheckCircle,
  Rows3,
  FlipHorizontal
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import Layout2DCanvas from './components/Layout2DCanvas';
import Layout3DScene from './components/Layout3DScene';
import PlacementPanel from './components/PlacementPanel';
import {
  WarehouseLayout,
  RackPosition,
  RackDimensions,
  RackSummary,
  EditorState,
  ViewMode,
  HeatmapData,
  LocationActivity,
  PreviewRackPosition,
  PlacementConfig,
  DEFAULT_EDITOR_STATE,
  DEFAULT_WAREHOUSE_DIMENSIONS,
  DEFAULT_GRID_UNIT,
  DEFAULT_PLACEMENT_CONFIG,
  DEFAULT_RUBBER_BAND
} from './types/warehouseLayout';
import {
  getLayout,
  saveLayout,
  createEmptyLayout,
  exportLayoutAsJson,
  importLayoutFromJson
} from './utils/layoutStorage';
import { calculateRackDimensions, findOptimalPosition } from './utils/positionCalculator';
import {
  calculatePreviewPositions,
  applyPreviewPositions,
  sortRacksByNumber
} from './utils/bulkPlacement';
import { stcBinService } from '../../../../services/stcBinService';

interface WarehouseLayoutEditorProps {
  onBack?: () => void;
}

const WarehouseLayoutEditor: React.FC<WarehouseLayoutEditorProps> = ({ onBack }) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';
  const warehouseId = '1';

  // State
  const [layout, setLayout] = useState<WarehouseLayout | null>(null);
  const [editorState, setEditorState] = useState<EditorState>(DEFAULT_EDITOR_STATE);
  const [rackSummaries, setRackSummaries] = useState<RackSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showSettings, setShowSettings] = useState(false);
  const [unplacedRacks, setUnplacedRacks] = useState<RackSummary[]>([]);

  // Bulk placement state
  const [showPlacementPanel, setShowPlacementPanel] = useState(false);
  const [selectedUnplacedRacks, setSelectedUnplacedRacks] = useState<string[]>([]);
  const [previewPositions, setPreviewPositions] = useState<PreviewRackPosition[]>([]);

  // Activity data (Phase 2)
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [activities, setActivities] = useState<LocationActivity[]>([]);

  // Calculate rack dimensions from summaries
  const rackDimensionsMap = useMemo(() => {
    const map = new Map<string, RackDimensions>();
    rackSummaries.forEach(summary => {
      map.set(summary.rackNumber, calculateRackDimensions(summary));
    });
    return map;
  }, [rackSummaries]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [warehouseId]);

  // Update unplaced racks when layout or summaries change
  useEffect(() => {
    if (layout && rackSummaries.length > 0) {
      const placedRackNumbers = new Set(layout.racks.map(r => r.rackNumber));
      const unplaced = rackSummaries.filter(s => !placedRackNumbers.has(s.rackNumber));
      setUnplacedRacks(sortRacksByNumber(unplaced));
    }
  }, [layout, rackSummaries]);

  // Update preview positions when placement config or anchor changes
  useEffect(() => {
    if (layout && editorState.anchorRack && selectedUnplacedRacks.length > 0) {
      const anchorRack = layout.racks.find(r => r.rackNumber === editorState.anchorRack);
      if (anchorRack) {
        const previews = calculatePreviewPositions(
          anchorRack,
          selectedUnplacedRacks,
          rackDimensionsMap,
          layout.racks,
          layout.dimensions,
          editorState.placementConfig,
          layout.gridUnit
        );
        setPreviewPositions(previews);
      }
    } else {
      setPreviewPositions([]);
    }
  }, [layout, editorState.anchorRack, editorState.placementConfig, selectedUnplacedRacks, rackDimensionsMap]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const summaries = await stcBinService.getRackSummary(companyNo, warehouseId);
      setRackSummaries(summaries);

      let storedLayout = getLayout(warehouseId);
      if (!storedLayout) {
        storedLayout = createEmptyLayout(warehouseId, `Lager ${warehouseId}`);
      }
      setLayout(storedLayout);

    } catch (err) {
      console.error('Error loading data:', err);
      setError('Kunne ikke laste lagerdata');
      setLayout(createEmptyLayout(warehouseId));
    } finally {
      setLoading(false);
    }
  };

  // Auto-save on layout changes
  useEffect(() => {
    if (layout && !loading) {
      const timeoutId = setTimeout(() => {
        handleSave();
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  }, [layout]);

  const handleSave = useCallback(() => {
    if (!layout) return;

    try {
      setSaveStatus('saving');
      saveLayout(layout);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving layout:', err);
      setSaveStatus('error');
    }
  }, [layout]);

  const handleLayoutChange = useCallback((newLayout: WarehouseLayout) => {
    setLayout(newLayout);
  }, []);

  const handleEditorStateChange = useCallback((newState: EditorState) => {
    setEditorState(newState);
  }, []);

  const handleSelectionChange = useCallback((selectedRacks: Set<string>) => {
    setEditorState(prev => ({ ...prev, selectedRacks }));
  }, []);

  const handleViewModeChange = (mode: ViewMode) => {
    setEditorState(prev => ({ ...prev, viewMode: mode }));
  };

  const handleExport = () => {
    if (layout) {
      exportLayoutAsJson(layout);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const importedLayout = await importLayoutFromJson(file);
      importedLayout.warehouseId = warehouseId;
      setLayout(importedLayout);
      handleSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import feilet');
    }

    e.target.value = '';
  };

  const handleAddRack = (rackNumber: string) => {
    if (!layout) return;

    const summary = rackSummaries.find(s => s.rackNumber === rackNumber);
    if (!summary) return;

    const dimensions = calculateRackDimensions(summary);
    const position = findOptimalPosition(
      layout.racks,
      rackDimensionsMap,
      dimensions,
      layout.dimensions,
      layout.gridUnit
    );

    if (!position) {
      setError('Kunne ikke finne plass til reol');
      return;
    }

    const newRack: RackPosition = {
      rackNumber,
      x: position.x,
      y: position.y,
      rotation: 0
    };

    setLayout({
      ...layout,
      racks: [...layout.racks, newRack]
    });
  };

  const handleAddAllRacks = () => {
    if (!layout || unplacedRacks.length === 0) return;

    let currentLayout = { ...layout };
    const newRacks: RackPosition[] = [...layout.racks];
    let failedCount = 0;

    for (const summary of unplacedRacks) {
      const dimensions = calculateRackDimensions(summary);
      const currentDimensionsMap = new Map(rackDimensionsMap);
      newRacks.forEach(r => {
        const sum = rackSummaries.find(s => s.rackNumber === r.rackNumber);
        if (sum) currentDimensionsMap.set(r.rackNumber, calculateRackDimensions(sum));
      });

      const position = findOptimalPosition(
        newRacks,
        currentDimensionsMap,
        dimensions,
        currentLayout.dimensions,
        currentLayout.gridUnit
      );

      if (position) {
        newRacks.push({
          rackNumber: summary.rackNumber,
          x: position.x,
          y: position.y,
          rotation: 0
        });
      } else {
        failedCount++;
      }
    }

    setLayout({
      ...currentLayout,
      racks: newRacks
    });

    if (failedCount > 0) {
      setError(`${failedCount} reoler kunne ikke plasseres automatisk (for liten plass)`);
    }
  };

  const handleRemoveRack = (rackNumber: string) => {
    if (!layout) return;

    setLayout({
      ...layout,
      racks: layout.racks.filter(r => r.rackNumber !== rackNumber)
    });

    // Clear from selection
    const newSelection = new Set(editorState.selectedRacks);
    newSelection.delete(rackNumber);
    setEditorState(prev => ({
      ...prev,
      selectedRacks: newSelection,
      anchorRack: prev.anchorRack === rackNumber ? null : prev.anchorRack
    }));
  };

  const handleRotateRack = (rackNumber: string) => {
    if (!layout) return;

    setLayout({
      ...layout,
      racks: layout.racks.map(r =>
        r.rackNumber === rackNumber
          ? { ...r, rotation: ((r.rotation + 90) % 360) as 0 | 90 | 180 | 270 }
          : r
      )
    });
  };

  const handleSetAnchor = (rackNumber: string) => {
    setEditorState(prev => ({
      ...prev,
      anchorRack: prev.anchorRack === rackNumber ? null : rackNumber
    }));
  };

  const handleResetLayout = () => {
    if (window.confirm('Er du sikker på at du vil nullstille layouten? Alle reolplasseringer vil bli fjernet.')) {
      setLayout(createEmptyLayout(warehouseId, layout?.name));
      setEditorState(prev => ({ ...prev, selectedRacks: new Set(), anchorRack: null }));
    }
  };

  const handleDimensionsChange = (field: 'length' | 'width' | 'height', value: number) => {
    if (!layout) return;
    setLayout({
      ...layout,
      dimensions: {
        ...layout.dimensions,
        [field]: value
      }
    });
  };

  const handlePlacementConfigChange = (config: PlacementConfig) => {
    setEditorState(prev => ({
      ...prev,
      placementConfig: config
    }));
  };

  const handleToggleUnplacedSelection = (rackNumber: string) => {
    setSelectedUnplacedRacks(prev => {
      if (prev.includes(rackNumber)) {
        return prev.filter(r => r !== rackNumber);
      }
      return [...prev, rackNumber];
    });
  };

  const handleSelectAllUnplaced = () => {
    setSelectedUnplacedRacks(unplacedRacks.map(r => r.rackNumber));
  };

  const handleDeselectAllUnplaced = () => {
    setSelectedUnplacedRacks([]);
  };

  const handleApplyBulkPlacement = () => {
    if (!layout || previewPositions.length === 0) return;

    const validPositions = applyPreviewPositions(previewPositions);
    if (validPositions.length === 0) {
      setError('Ingen gyldige plasseringer å anvende');
      return;
    }

    // Add new racks to layout
    setLayout({
      ...layout,
      racks: [...layout.racks, ...validPositions]
    });

    // Clear bulk placement state
    setSelectedUnplacedRacks([]);
    setPreviewPositions([]);
    setEditorState(prev => ({ ...prev, anchorRack: null }));
    setShowPlacementPanel(false);
  };

  const handleCancelBulkPlacement = () => {
    setSelectedUnplacedRacks([]);
    setPreviewPositions([]);
    setEditorState(prev => ({ ...prev, anchorRack: null }));
    setShowPlacementPanel(false);
  };

  const toggleOption = (option: keyof EditorState) => {
    setEditorState(prev => ({
      ...prev,
      [option]: !prev[option as keyof EditorState]
    }));
  };

  // Delete selected racks
  const handleDeleteSelected = () => {
    if (!layout || editorState.selectedRacks.size === 0) return;

    setLayout({
      ...layout,
      racks: layout.racks.filter(r => !editorState.selectedRacks.has(r.rackNumber))
    });
    setEditorState(prev => ({
      ...prev,
      selectedRacks: new Set(),
      anchorRack: editorState.selectedRacks.has(prev.anchorRack || '') ? null : prev.anchorRack
    }));
  };

  // Rotate selected racks
  const handleRotateSelected = () => {
    if (!layout || editorState.selectedRacks.size === 0) return;

    setLayout({
      ...layout,
      racks: layout.racks.map(rack => {
        if (editorState.selectedRacks.has(rack.rackNumber)) {
          return {
            ...rack,
            rotation: ((rack.rotation + 90) % 360) as 0 | 90 | 180 | 270
          };
        }
        return rack;
      })
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Laster lagerdata...</p>
        </div>
      </div>
    );
  }

  if (!layout) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600">Kunne ikke laste layout</p>
          <button
            onClick={loadData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Tilbake"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-900">3D Lagervisning</h1>
              <p className="text-sm text-gray-500">
                {layout.name} • {layout.racks.length} reoler plassert
                {editorState.selectedRacks.size > 0 && (
                  <span className="text-blue-600 ml-2">
                    ({editorState.selectedRacks.size} valgt)
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => handleViewModeChange('2d')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                editorState.viewMode === '2d'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              2D Editor
            </button>
            <button
              onClick={() => handleViewModeChange('3d')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                editorState.viewMode === '3d'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              3D Visning
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <span className="text-sm text-gray-500 flex items-center gap-1">
                <Loader2 className="w-4 h-4 animate-spin" />
                Lagrer...
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="text-sm text-green-600 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" />
                Lagret
              </span>
            )}

            <button onClick={handleSave} className="p-2 hover:bg-gray-100 rounded-lg" title="Lagre">
              <Save className="w-5 h-5" />
            </button>
            <button onClick={handleExport} className="p-2 hover:bg-gray-100 rounded-lg" title="Eksporter JSON">
              <Download className="w-5 h-5" />
            </button>
            <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer" title="Importer JSON">
              <Upload className="w-5 h-5" />
              <input type="file" accept=".json" onChange={handleImport} className="hidden" />
            </label>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg ${showSettings ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title="Innstillinger"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left sidebar */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          {/* Toolbar */}
          <div className="p-3 border-b border-gray-200">
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => toggleOption('showGrid')}
                className={`p-2 rounded ${editorState.showGrid ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
                title="Vis rutenett"
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleOption('showLabels')}
                className={`p-2 rounded ${editorState.showLabels ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
                title="Vis etiketter"
              >
                <Tag className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleOption('showHeatmap')}
                className={`p-2 rounded ${editorState.showHeatmap ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100'}`}
                title="Vis heatmap"
              >
                <Flame className="w-4 h-4" />
              </button>
              <button
                onClick={() => toggleOption('showActivity')}
                className={`p-2 rounded ${editorState.showActivity ? 'bg-green-100 text-green-600' : 'hover:bg-gray-100'}`}
                title="Vis aktivitet"
              >
                <Radio className="w-4 h-4" />
              </button>
              <div className="w-px h-6 bg-gray-200 mx-1 self-center" />
              {editorState.selectedRacks.size > 0 && (
                <>
                  <button
                    onClick={handleRotateSelected}
                    className="p-2 rounded hover:bg-gray-100"
                    title="Roter valgte (R)"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                  <button
                    onClick={handleDeleteSelected}
                    className="p-2 rounded hover:bg-red-100 text-red-600"
                    title="Slett valgte (Delete)"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
              <button
                onClick={handleResetLayout}
                className="p-2 rounded hover:bg-red-100 text-red-600"
                title="Nullstill layout"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Placed racks */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-3">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                Plasserte reoler ({layout.racks.length})
              </h3>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {layout.racks.map(rack => {
                  const dim = rackDimensionsMap.get(rack.rackNumber);
                  const isSelected = editorState.selectedRacks.has(rack.rackNumber);
                  const isAnchor = editorState.anchorRack === rack.rackNumber;
                  return (
                    <div
                      key={rack.rackNumber}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        isAnchor
                          ? 'bg-green-100 border-2 border-green-400'
                          : isSelected
                          ? 'bg-blue-100 border-2 border-blue-400'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      onClick={() => handleSelectionChange(new Set([rack.rackNumber]))}
                    >
                      <div>
                        <div className="font-medium text-sm flex items-center gap-1">
                          {isAnchor && <span className="text-green-600">⚓</span>}
                          {rack.rackNumber}
                        </div>
                        <div className="text-xs text-gray-500">
                          {dim ? `${dim.verticals}×${dim.shelves}` : ''} • {rack.rotation}°
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSetAnchor(rack.rackNumber);
                          }}
                          className={`p-1 rounded ${isAnchor ? 'bg-green-200' : 'hover:bg-gray-200'}`}
                          title={isAnchor ? 'Fjern som anker' : 'Sett som anker'}
                        >
                          <span className="text-xs">⚓</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRotateRack(rack.rackNumber);
                          }}
                          className="p-1 hover:bg-gray-200 rounded"
                          title="Roter"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveRack(rack.rackNumber);
                          }}
                          className="p-1 hover:bg-red-100 text-red-600 rounded"
                          title="Fjern"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Unplaced racks */}
            {unplacedRacks.length > 0 && (
              <div className="p-3 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-700">
                    Ikke plassert ({unplacedRacks.length})
                  </h3>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setShowPlacementPanel(!showPlacementPanel)}
                      className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                        showPlacementPanel
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                      title="Bulk-plassering"
                    >
                      <Rows3 className="w-3 h-3" />
                      Bulk
                    </button>
                    <button
                      onClick={handleAddAllRacks}
                      className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                      title="Plasser alle automatisk"
                    >
                      <Plus className="w-3 h-3" />
                      Auto
                    </button>
                  </div>
                </div>

                {/* Bulk selection controls */}
                {showPlacementPanel && (
                  <div className="mb-2 flex gap-2">
                    <button
                      onClick={handleSelectAllUnplaced}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      Velg alle
                    </button>
                    <button
                      onClick={handleDeselectAllUnplaced}
                      className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded"
                    >
                      Fjern valg
                    </button>
                    <span className="text-xs text-gray-500 self-center">
                      {selectedUnplacedRacks.length} valgt
                    </span>
                  </div>
                )}

                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {unplacedRacks.map(summary => {
                    const isSelected = selectedUnplacedRacks.includes(summary.rackNumber);
                    return (
                      <div
                        key={summary.rackNumber}
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          showPlacementPanel
                            ? isSelected
                              ? 'bg-blue-100 border-2 border-blue-400'
                              : 'bg-yellow-50 hover:bg-yellow-100 cursor-pointer'
                            : 'bg-yellow-50'
                        }`}
                        onClick={() => showPlacementPanel && handleToggleUnplacedSelection(summary.rackNumber)}
                      >
                        <div className="flex items-center gap-2">
                          {showPlacementPanel && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleUnplacedSelection(summary.rackNumber)}
                              className="w-4 h-4 rounded border-gray-300"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                          <div>
                            <div className="font-medium text-sm">{summary.rackNumber}</div>
                            <div className="text-xs text-gray-500">
                              {summary.maxVertical - summary.minVertical + 1}×
                              {summary.maxShelf - summary.minShelf + 1} •{' '}
                              {summary.locationCount} lok.
                            </div>
                          </div>
                        </div>
                        {!showPlacementPanel && (
                          <button
                            onClick={() => handleAddRack(summary.rackNumber)}
                            className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                            title="Plasser reol"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Settings panel */}
          {showSettings && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Varehus-dimensjoner</h3>
              <div className="space-y-2">
                <div>
                  <label className="text-xs text-gray-600">Lengde (m)</label>
                  <input
                    type="number"
                    value={layout.dimensions.length}
                    onChange={(e) => handleDimensionsChange('length', Number(e.target.value))}
                    className="w-full px-2 py-1 text-sm border rounded"
                    min={10}
                    max={200}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Bredde (m)</label>
                  <input
                    type="number"
                    value={layout.dimensions.width}
                    onChange={(e) => handleDimensionsChange('width', Number(e.target.value))}
                    className="w-full px-2 py-1 text-sm border rounded"
                    min={10}
                    max={200}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Høyde (m)</label>
                  <input
                    type="number"
                    value={layout.dimensions.height}
                    onChange={(e) => handleDimensionsChange('height', Number(e.target.value))}
                    className="w-full px-2 py-1 text-sm border rounded"
                    min={3}
                    max={20}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Main canvas/scene area */}
        <div className="flex-1 min-w-0 p-4 flex flex-col">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2 flex-shrink-0">
              <AlertCircle className="w-5 h-5" />
              {error}
              <button onClick={() => setError(null)} className="ml-auto text-red-800 hover:text-red-900">
                ×
              </button>
            </div>
          )}

          <div className="flex-1 min-h-0 rounded-xl overflow-hidden shadow-lg">
            {editorState.viewMode === '2d' ? (
              <Layout2DCanvas
                layout={layout}
                editorState={editorState}
                rackSummaries={rackSummaries}
                onLayoutChange={handleLayoutChange}
                onEditorStateChange={handleEditorStateChange}
                onSelectionChange={handleSelectionChange}
                previewPositions={previewPositions}
                heatmapData={heatmapData}
                activities={activities}
              />
            ) : (
              <Layout3DScene
                layout={layout}
                rackSummaries={rackSummaries}
                rackDimensions={rackDimensionsMap}
                editorState={editorState}
                onRackSelect={(rackNumber) => handleSelectionChange(rackNumber ? new Set([rackNumber]) : new Set())}
                heatmapData={heatmapData}
                activities={activities}
              />
            )}
          </div>
        </div>

        {/* Placement Panel (right sidebar) */}
        <PlacementPanel
          config={editorState.placementConfig}
          onConfigChange={handlePlacementConfigChange}
          selectedUnplacedRacks={selectedUnplacedRacks}
          anchorRack={editorState.anchorRack}
          previewPositions={previewPositions}
          onApply={handleApplyBulkPlacement}
          onCancel={handleCancelBulkPlacement}
          onSetAnchor={() => {
            if (layout && layout.racks.length > 0) {
              handleSetAnchor(layout.racks[0].rackNumber);
            }
          }}
          unplacedRacks={unplacedRacks}
          isVisible={showPlacementPanel}
        />
      </div>

      {/* Selected rack info bar */}
      {editorState.selectedRacks.size === 1 && (
        <div className="bg-white border-t border-gray-200 px-4 py-2">
          <div className="flex items-center gap-4">
            <Box className="w-5 h-5 text-blue-600" />
            <span className="font-medium">Reol {Array.from(editorState.selectedRacks)[0]}</span>
            {(() => {
              const rackNumber = Array.from(editorState.selectedRacks)[0];
              const rack = layout.racks.find(r => r.rackNumber === rackNumber);
              const dim = rackDimensionsMap.get(rackNumber);
              if (rack && dim) {
                return (
                  <>
                    <span className="text-gray-500">•</span>
                    <span className="text-sm text-gray-600">
                      Posisjon: ({rack.x.toFixed(1)}m, {rack.y.toFixed(1)}m)
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-sm text-gray-600">
                      Størrelse: {dim.width.toFixed(1)}m × {dim.depth.toFixed(1)}m × {dim.height.toFixed(1)}m
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-sm text-gray-600">
                      Rotasjon: {rack.rotation}°
                    </span>
                  </>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}

      {editorState.selectedRacks.size > 1 && (
        <div className="bg-white border-t border-gray-200 px-4 py-2">
          <div className="flex items-center gap-4">
            <Box className="w-5 h-5 text-blue-600" />
            <span className="font-medium">{editorState.selectedRacks.size} reoler valgt</span>
            <span className="text-gray-500">•</span>
            <span className="text-sm text-gray-600">
              Trykk R for å rotere alle, Delete for å slette
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseLayoutEditor;
